import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Info, Search, Settings } from "lucide-react";
import { WorkoutContext } from "../../../contexts/WorkoutContext";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { LLM_PROVIDERS, getModelDisplayName } from "../../../config/llmProviders";
import {
  getProviderApiKey,
  getReadyProviderOptions,
  getQuickSwitchModels,
  getNextQuickSwitchModel,
} from "../../../utils/llmQuickSwitch";
import { firstName, isOnShare, shareModels, shareTitle } from "../../../utils/creditShares";

/** Whether the selected provider still needs an API key, and its label. */
export const useProviderStatus = () => {
  const { llmSettings } = useContext(WorkoutContext);
  const providerConfig = LLM_PROVIDERS[llmSettings?.provider];
  const onShare = isOnShare(llmSettings);
  const needsApiKey =
    !onShare &&
    providerConfig?.apiKeyRequired &&
    !llmSettings?.apiKey &&
    !llmSettings?.apiKeys?.[llmSettings?.provider];
  const providerLabel = onShare
    ? shareTitle(llmSettings.creditShare)
    : providerConfig?.label || llmSettings?.provider || "Unknown";
  return { needsApiKey, providerLabel };
};

/**
 * The "Using <provider> · <model>" quick switcher under the Generate button.
 * Moved out of GenerateSessionDialog unchanged; `onOpenSettings` closes the
 * dialog and opens AI Settings.
 */
const ModelSwitcher = ({ onOpenSettings }) => {
  const { llmSettings, setLlmSettings, money } = useContext(WorkoutContext);
  const [footerSwitcherOpen, setFooterSwitcherOpen] = useState(false);
  const [footerProvider, setFooterProvider] = useState(
    llmSettings?.provider || "ollama",
  );
  const [providerFilter, setProviderFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [localModels, setLocalModels] = useState([]);
  const [isLoadingLocalModels, setIsLoadingLocalModels] = useState(false);
  const [localModelsError, setLocalModelsError] = useState(null);
  const footerSwitcherContentRef = useRef(null);

  // On a mate's shared credits the switcher only offers the models they
  // shared; changing provider is done in AI engine settings.
  const onShare = isOnShare(llmSettings);
  const { needsApiKey, providerLabel } = useProviderStatus();
  const readyProviderOptions = useMemo(
    () => getReadyProviderOptions(llmSettings, LLM_PROVIDERS),
    [llmSettings],
  );
  const currentProviderReady =
    onShare ||
    readyProviderOptions.some(
      (provider) => provider.key === llmSettings?.provider,
    );
  const hiddenProviderCount = Math.max(
    0,
    Object.keys(LLM_PROVIDERS).length - readyProviderOptions.length,
  );

  const fetchLocalModels = useCallback(async () => {
    setIsLoadingLocalModels(true);
    setLocalModelsError(null);

    try {
      const response = await fetch("/ollama-api/tags");
      if (!response.ok) {
        throw new Error("Could not reach Ollama to load local models.");
      }

      const data = await response.json();
      setLocalModels((data.models || []).map((model) => model.name));
    } catch (err) {
      setLocalModels([]);
      setLocalModelsError(
        err?.message || "Could not reach Ollama to load local models.",
      );
    } finally {
      setIsLoadingLocalModels(false);
    }
  }, []);

  useEffect(() => {
    const fallbackProvider = currentProviderReady
      ? llmSettings?.provider
      : readyProviderOptions[0]?.key || llmSettings?.provider || "ollama";

    setFooterProvider(fallbackProvider);
  }, [currentProviderReady, llmSettings?.provider, readyProviderOptions]);

  useEffect(() => {
    if (footerProvider === "ollama") {
      fetchLocalModels();
    } else {
      setLocalModelsError(null);
    }
    // A fresh provider means a fresh model list — drop any stale search term.
    setModelFilter("");
  }, [fetchLocalModels, footerProvider]);

  const footerModelOptions = useMemo(
    () =>
      onShare
        ? shareModels(llmSettings.creditShare)
        : getQuickSwitchModels({
            provider: footerProvider,
            providers: LLM_PROVIDERS,
            localModels,
            currentSettings: llmSettings,
          }),
    [onShare, footerProvider, localModels, llmSettings],
  );

  // Filtered views for the quick-switcher search boxes. Provider search only
  // matters once several providers are configured; model search is the main
  // one (Ollama can expose many local models).
  const filteredProviderOptions = useMemo(() => {
    const q = providerFilter.trim().toLowerCase();
    if (!q) return readyProviderOptions;
    return readyProviderOptions.filter((provider) =>
      `${provider.label} ${provider.key}`.toLowerCase().includes(q),
    );
  }, [readyProviderOptions, providerFilter]);

  const filteredModelOptions = useMemo(() => {
    const q = modelFilter.trim().toLowerCase();
    if (!q) return footerModelOptions;
    return footerModelOptions.filter((modelName) =>
      `${modelName} ${getModelDisplayName(footerProvider, modelName, money)}`
        .toLowerCase()
        .includes(q),
    );
  }, [footerModelOptions, modelFilter, footerProvider, money]);

  const footerSelectedModel = useMemo(
    () =>
      getNextQuickSwitchModel({
        provider: footerProvider,
        providers: LLM_PROVIDERS,
        localModels,
        currentSettings: llmSettings,
      }),
    [footerProvider, localModels, llmSettings],
  );

  const openAiSettings = useCallback(() => {
    setFooterSwitcherOpen(false);
    onOpenSettings?.();
  }, [onOpenSettings]);

  const persistQuickSwitch = useCallback(
    (provider, model) => {
      const share = llmSettings?.creditShare;
      setLlmSettings({
        ...llmSettings,
        provider,
        model,
        apiKey:
          provider === "ollama"
            ? ""
            : getProviderApiKey(llmSettings, provider),
        creditShare: share && share.provider === provider ? share : null,
      });
    },
    [llmSettings, setLlmSettings],
  );

  const handleFooterProviderChange = useCallback(
    (provider) => {
      setFooterProvider(provider);

      persistQuickSwitch(
        provider,
        getNextQuickSwitchModel({
          provider,
          providers: LLM_PROVIDERS,
          localModels,
          currentSettings: llmSettings,
        }),
      );
    },
    [llmSettings, localModels, persistQuickSwitch],
  );

  const handleFooterModelChange = useCallback(
    (model) => {
      persistQuickSwitch(footerProvider, model);
      setFooterSwitcherOpen(false);
    },
    [footerProvider, persistQuickSwitch],
  );

  return (
    <div className="flex w-full min-w-0 items-center justify-center gap-2 truncate font-mono text-[10px] text-muted-foreground/80">
                <span
                  className={`inline-block h-1.5 w-1.5 rounded-full shrink-0 shadow-[0_0_5px_currentColor] ${needsApiKey ? "bg-yellow-500 text-yellow-500" : "bg-primary text-primary"}`}
                />
                <Popover
                  open={footerSwitcherOpen}
                  onOpenChange={(next) => {
                    setFooterSwitcherOpen(next);
                    if (!next) {
                      setProviderFilter("");
                      setModelFilter("");
                    }
                  }}
                  modal
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto max-w-full gap-1 px-2 py-1 text-[10px] font-mono text-muted-foreground/80 hover:bg-primary/10 hover:text-foreground"
                    >
                      <span className="truncate">
                        Using {providerLabel}
                        {llmSettings?.model ? ` \u00b7 ${llmSettings.model}` : ""}
                      </span>
                      <ChevronDown className="h-3 w-3 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    ref={footerSwitcherContentRef}
                    align="center"
                    collisionPadding={12}
                    // Radix otherwise focuses the first tabbable child — the
                    // info button — which pops its tooltip open over the
                    // provider list. Park focus on the panel instead so the
                    // focus trap still works and nothing is revealed.
                    onOpenAutoFocus={(event) => {
                      event.preventDefault();
                      footerSwitcherContentRef.current?.focus();
                    }}
                    className="w-[min(24rem,calc(100vw_-_3rem))] max-h-[75dvh] space-y-3 overflow-y-auto p-3"
                  >
                    <div className="flex items-center gap-1.5">
                      <p className="text-xs font-semibold tracking-wide text-foreground">
                        AI Engine
                      </p>
                      {/* CSS-only tooltip (no Radix dep / no extra layer inside
                          the modal): reveals help on hover or keyboard focus.
                          Keyed off `peer-focus-visible`, not `focus-within` —
                          Radix moves focus to this button when the popover
                          opens, and `focus-within` made the tooltip appear
                          unprompted on top of the provider buttons. */}
                      <span className="relative inline-flex">
                        <button
                          type="button"
                          aria-label="About the AI engine switcher"
                          className="peer inline-flex text-muted-foreground/70 transition-colors hover:text-foreground focus:outline-none focus-visible:text-foreground"
                        >
                          <Info className="h-3.5 w-3.5" />
                        </button>
                        <span
                          role="tooltip"
                          className="pointer-events-none absolute left-0 top-full z-10 mt-1.5 w-56 rounded-md border border-border/60 bg-popover px-2.5 py-1.5 text-[11px] leading-relaxed text-popover-foreground opacity-0 shadow-md transition-opacity duration-150 peer-hover:opacity-100 peer-focus-visible:opacity-100"
                        >
                          Switch the provider and model used for generation. Only
                          providers that are ready to use right now are shown.
                        </span>
                      </span>
                    </div>

                    {!currentProviderReady && (
                      <div className="rounded-md border border-yellow-500/30 bg-yellow-500/10 px-2.5 py-2 text-[11px] leading-relaxed text-yellow-500">
                        The current provider is not fully configured. Pick a
                        ready provider below or open AI engine settings.
                      </div>
                    )}

                    {onShare && (
                      <p className="text-[11px] leading-relaxed text-muted-foreground">
                        On {firstName(llmSettings.creditShare.ownerName)}&rsquo;s
                        shared credits. Change provider in AI engine settings.
                      </p>
                    )}

                    {!onShare && (
                    <div className="space-y-2">
                      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        Provider
                      </Label>
                      {readyProviderOptions.length > 6 && (
                        <div className="relative">
                          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            value={providerFilter}
                            onChange={(e) => setProviderFilter(e.target.value)}
                            placeholder="Filter providers..."
                            className="h-8 pl-7 text-xs bg-background/60"
                          />
                        </div>
                      )}
                      <div className="flex max-h-28 flex-wrap gap-2 overflow-y-auto">
                        {filteredProviderOptions.length > 0 ? (
                          filteredProviderOptions.map((provider) => {
                            const isActive = provider.key === footerProvider;

                            return (
                              <Button
                                key={provider.key}
                                type="button"
                                variant="outline"
                                size="sm"
                                className={`h-8 max-w-full text-xs ${
                                  isActive
                                    ? "border-primary/60 bg-primary/10 text-primary"
                                    : "border-border/50 bg-background/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                                }`}
                                onClick={() =>
                                  handleFooterProviderChange(provider.key)
                                }
                              >
                                <span className="truncate">
                                  {provider.label}
                                </span>
                              </Button>
                            );
                          })
                        ) : (
                          <p className="px-1 py-1 text-xs text-muted-foreground">
                            No providers match your search.
                          </p>
                        )}
                      </div>
                    </div>
                    )}

                    <div className="space-y-2">
                      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        Model
                      </Label>
                      {footerModelOptions.length > 5 && (
                        <div className="relative">
                          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            value={modelFilter}
                            onChange={(e) => setModelFilter(e.target.value)}
                            placeholder="Filter models..."
                            className="h-8 pl-7 text-xs bg-background/60"
                          />
                        </div>
                      )}
                      <div className="max-h-56 overflow-y-auto rounded-md border border-border/40 bg-background/40 p-1">
                        {filteredModelOptions.length > 0 ? (
                          filteredModelOptions.map((modelName) => {
                            const isActive = modelName === footerSelectedModel;

                            return (
                              <button
                                key={modelName}
                                type="button"
                                onClick={() => handleFooterModelChange(modelName)}
                                className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs transition-colors hover:bg-accent ${
                                  isActive
                                    ? "bg-primary/10 text-primary"
                                    : "text-foreground"
                                }`}
                              >
                                <Check
                                  className={`h-3.5 w-3.5 shrink-0 ${
                                    isActive ? "opacity-100" : "opacity-0"
                                  }`}
                                />
                                <span className="truncate">
                                  {getModelDisplayName(footerProvider, modelName, money)}
                                </span>
                              </button>
                            );
                          })
                        ) : (
                          <div className="px-2 py-1 text-xs text-muted-foreground select-none bg-muted/20">
                            {footerModelOptions.length > 0
                              ? "No models match your search."
                              : isLoadingLocalModels &&
                                  footerProvider === "ollama"
                                ? "Loading local models..."
                                : "No models available for quick switching."}
                          </div>
                        )}
                      </div>
                      {footerProvider === "ollama" && localModelsError && (
                        <p className="text-[10px] leading-relaxed text-yellow-500">
                          {localModelsError}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5 border-t border-border/40 pt-2">
                      {hiddenProviderCount > 0 && !onShare && (
                        <p className="text-[10px] leading-relaxed text-muted-foreground">
                          {hiddenProviderCount} more provider
                          {hiddenProviderCount === 1 ? "" : "s"} available
                          {" \u2014 "}add keys in AI engine settings.
                        </p>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 w-full"
                        onClick={openAiSettings}
                      >
                        <Settings className="mr-1.5 h-3.5 w-3.5" />
                        AI engine settings
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
    </div>
  );
};

export default ModelSwitcher;
